import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { baseQueryCreator } from "redux/baseQuery";
import env from "env";
import {
  GenerateKeyRequest,
  GenerateKeyParams,
  GenerateKeyResponse,
  CustomerOptionsRequest,
  CustomerOptionsParams,
  CustomerOptionsResponse,
  ListKeysResponse,
  RemoveKeyRequest,
  RemoveKeyResponse,
  ValidationSettingsRequest,
  ValidationSettingsResponse,
  ValidationSettingsFullResponse,
  GetSettingsResponse,
  ValidationResult,
} from "./types";

const { cryptographyService } = env.api.baseUrl;
// Fallback to hardcoded URL if env variable not set
const fileValidationService = env.api.baseUrl.fileValidationService || 'https://api-enterprise-payroll.gotymeph.devtymex.io/file-validation-service/1.0.0/';

// Create base query with proper authentication for cryptography service
// Uses JWT Bearer token and Org-Token header, with organizationId as query parameter
const cryptographyBaseQuery = fetchBaseQuery({
  baseUrl: cryptographyService,
  prepareHeaders: (headers, { getState }: any) => {
    const token = getState().auth.accessToken;
    const orgData = getState().auth.organisationData;

    if (token) {
      headers.set("Authorization", `Bearer ${token}`);
    }

    if (orgData?.orgToken) {
      headers.set("Org-Token", orgData.orgToken);
    }

    headers.set("Content-Type", "application/json");
    return headers;
  },
});

// Create base query for file validation service
// Uses JWT Bearer token and Org-Token header, with organizationId as path parameter for consistency
const fileValidationBaseQuery = fetchBaseQuery({
  baseUrl: fileValidationService,
  prepareHeaders: (headers, { getState }: any) => {
    const token = getState().auth.accessToken;
    const orgData = getState().auth.organisationData;

    if (token) {
      headers.set("Authorization", `Bearer ${token}`);
    }

    if (orgData?.orgToken) {
      headers.set("Org-Token", orgData.orgToken);
    }

    headers.set("Content-Type", "application/json");
    return headers;
  },
});

// Create separate API for validation settings
const validationApi = createApi({
  reducerPath: "validationApi",
  baseQuery: baseQueryCreator(fileValidationBaseQuery),
  tagTypes: ["ValidationSettings", "ValidationResults"],
  endpoints: (builder) => ({
    updateValidationSettings: builder.mutation<ValidationSettingsResponse, { organizationId: string; validationSettings: ValidationSettingsRequest }>({
      query: ({ organizationId, validationSettings }) => ({
        url: `settings`,
        method: "POST",
        body: {
          organizationId,
          validationSettings,
        },
      }),
      invalidatesTags: ["ValidationSettings"],
    }),
    
    getValidationSettings: builder.query<ValidationSettingsRequest, string>({
      query: (organizationId) => ({
        url: `settings?organizationId=${organizationId}`,
        method: "GET",
      }),
      providesTags: ["ValidationSettings"],
      transformResponse: (response: any) => {
        // Handle both success and 404 responses
        // Backend returns { organizationId, validationSettings, createdAt, updatedAt } for success
        // Backend returns { organizationId, validationSettings: {}, message } for 404
        if (response && typeof response === 'object' && 'validationSettings' in response) {
          const validationSettings = response.validationSettings;
          // Return null if settings are empty (404 case)
          // This ensures the component can distinguish between "no data" and "empty object"
          if (!validationSettings || Object.keys(validationSettings).length === 0) {
            return null;
          }
          return validationSettings;
        }
        // Return null for unexpected response format instead of empty object
        return null;
      },
    }),
    
    // New endpoint for dual polling strategy
    // APEX-1151: Query validation results by filename instead of UUID
    getValidationResults: builder.query<ValidationResult, string>({
      query: (filename) => ({
        url: `validation-results/${encodeURIComponent(filename)}`,
        method: "GET",
      }),
      providesTags: ["ValidationResults"],
    }),

    deleteValidationSettings: builder.mutation<{ status: string; message: string }, string>({
      query: (organizationId) => ({
        url: `settings`,
        method: "DELETE",
        body: {
          organizationId,
        },
      }),
      invalidatesTags: ["ValidationSettings"],
    }),
  }),
});

export const cryptographyApi = createApi({
  reducerPath: "cryptographyApi",
  baseQuery: baseQueryCreator(cryptographyBaseQuery),
  tagTypes: ["CryptoKeys", "CryptoSettings"],
  endpoints: (builder) => ({
    generateKey: builder.mutation<GenerateKeyResponse, GenerateKeyParams>({
      query: ({ organizationId, keyRequest }) => ({
        url: `keys`,
        method: "POST",
        body: {
          organizationId,
          ...keyRequest,
        },
      }),
      invalidatesTags: ["CryptoKeys"],
    }),
    
    listKeys: builder.query<ListKeysResponse, string>({
      query: (organizationId) => ({
        url: `keys?organizationId=${organizationId}`,
        method: "GET",
      }),
      providesTags: ["CryptoKeys"],
    }),
    
    removeKey: builder.mutation<RemoveKeyResponse, { keyId: string; organizationId: string; data: RemoveKeyRequest }>({
      query: ({ keyId, organizationId, data }) => ({
        url: `keys`,
        method: "DELETE",
        body: {
          organizationId,
          keyId,
        },
      }),
      invalidatesTags: ["CryptoKeys"],
    }),
    
    createEncryptionSettings: builder.mutation<CustomerOptionsResponse, CustomerOptionsParams>({
      query: ({ organizationId, settings }) => ({
        url: `settings`,
        method: "POST",
        body: {
          organizationId,
          settings,
        },
      }),
      invalidatesTags: ["CryptoSettings"],
    }),
    
    updateEncryptionSettings: builder.mutation<CustomerOptionsResponse, CustomerOptionsParams>({
      query: ({ organizationId, settings }) => ({
        url: `settings`,
        method: "PATCH",
        body: {
          organizationId,
          settings,
        },
      }),
      invalidatesTags: ["CryptoSettings"],
    }),
    
    getEncryptionSettings: builder.query<GetSettingsResponse, string>({
      query: (organizationId) => ({
        url: `settings?organizationId=${organizationId}`,
        method: "GET",
      }),
      providesTags: ["CryptoSettings"],
    }),

    deleteEncryptionSettings: builder.mutation<{ status: string; message: string }, string>({
      query: (organizationId) => ({
        url: `settings`,
        method: "DELETE",
        body: {
          organizationId,
        },
      }),
      invalidatesTags: ["CryptoSettings"],
    }),
  }),
});

export { validationApi };

export const {
  useGenerateKeyMutation,
  useListKeysQuery,
  useRemoveKeyMutation,
  useCreateEncryptionSettingsMutation,
  useUpdateEncryptionSettingsMutation,
  useGetEncryptionSettingsQuery,
  useDeleteEncryptionSettingsMutation,
} = cryptographyApi;

export const {
  useUpdateValidationSettingsMutation,
  useGetValidationSettingsQuery,
  useGetValidationResultsQuery,
  useDeleteValidationSettingsMutation,
} = validationApi;